import { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, RotateCcw, ExternalLink } from "lucide-react";
import axios from "axios";
import { useLang } from "../../i18n/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const STORAGE_KEY = "pt-chat";
const MAX_MESSAGES = 40; // 20 turns

/* ---------- helpers ---------- */
const esc = (s) =>
    s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));

/** Minimal, safe markdown: bold, lists, internal + https links, paragraphs. */
const md = (s) => {
    const h = esc(s)
        .replace(/\*\*(.+?)\*\*/g, "<b>$1</b>")
        .replace(
            /\[([^\]]+)\]\((\/[^\s)]*|https:\/\/[^\s)]+)\)/g,
            (_m, label, href) => {
                const external = href.startsWith("https://");
                const attrs = external
                    ? ` target="_blank" rel="noopener noreferrer"`
                    : "";
                return `<a href="${href}"${attrs} class="pt-chat-link">${label}</a>`;
            },
        );
    return h
        .split(/\n{2,}/)
        .map((b) => {
            if (/^\s*[-•] /m.test(b)) {
                return (
                    "<ul>" +
                    b
                        .split("\n")
                        .filter(Boolean)
                        .map((l) => "<li>" + l.replace(/^\s*[-•] /, "") + "</li>")
                        .join("") +
                    "</ul>"
                );
            }
            return "<p>" + b.replace(/\n/g, "<br>") + "</p>";
        })
        .join("");
};

const genSessionId = () =>
    "s_" + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);

/* ---------- Pentomino iso avatar ---------- */
const PentoAvatar = ({ size = 20 }) => (
    <svg
        viewBox="0 0 212 324"
        width={size}
        height={size * (324 / 212)}
        aria-hidden="true"
        className="flex-none"
    >
        <rect width="100" height="100" rx="12" fill="#ffffff" />
        <rect x="112" width="100" height="100" rx="12" fill="#ffffff" />
        <rect y="112" width="100" height="100" rx="12" fill="#ffffff" />
        <rect x="112" y="112" width="100" height="100" rx="12" fill="#ffffff" />
        <rect y="224" width="100" height="100" rx="12" fill="#F0464D" />
    </svg>
);

/* ---------- component ---------- */
export const ChatWidget = () => {
    const { t, lang, waUrl } = useLang();
    const c = t.chat;

    const [open, setOpen] = useState(false);
    const [sessionId, setSessionId] = useState("");
    const [messages, setMessages] = useState([]); // {role, content}
    const [pending, setPending] = useState(false);
    const [err, setErr] = useState("");
    const [draft, setDraft] = useState("");

    const logRef = useRef(null);
    const inputRef = useRef(null);

    // Hydrate session from localStorage once
    useEffect(() => {
        try {
            const raw = window.localStorage.getItem(STORAGE_KEY);
            if (raw) {
                const d = JSON.parse(raw);
                if (d.sessionId) setSessionId(d.sessionId);
                if (Array.isArray(d.messages)) setMessages(d.messages);
            }
            if (!window.localStorage.getItem(STORAGE_KEY)) {
                setSessionId(genSessionId());
            }
        } catch {
            setSessionId(genSessionId());
        }
    }, []);

    // Ensure we always have an id
    useEffect(() => {
        if (!sessionId) setSessionId(genSessionId());
    }, [sessionId]);

    // Persist on every change
    useEffect(() => {
        if (!sessionId) return;
        try {
            window.localStorage.setItem(
                STORAGE_KEY,
                JSON.stringify({
                    sessionId,
                    messages: messages.slice(-MAX_MESSAGES),
                }),
            );
        } catch {}
    }, [sessionId, messages]);

    // Auto-scroll on new message
    useEffect(() => {
        if (!open) return;
        const el = logRef.current;
        if (el) el.scrollTop = el.scrollHeight;
    }, [open, messages, pending]);

    // Focus on open
    useEffect(() => {
        if (open) {
            const id = setTimeout(() => inputRef.current?.focus(), 180);
            return () => clearTimeout(id);
        }
    }, [open]);

    // Esc to close
    useEffect(() => {
        if (!open) return;
        const h = (e) => {
            if (e.key === "Escape") setOpen(false);
        };
        window.addEventListener("keydown", h);
        return () => window.removeEventListener("keydown", h);
    }, [open]);

    const send = useCallback(
        async (text) => {
            const q = (text || "").trim();
            if (!q || pending || !sessionId) return;
            setErr("");
            setMessages((prev) => [...prev, { role: "user", content: q }]);
            setDraft("");
            setPending(true);
            try {
                const { data } = await axios.post(
                    `${API}/chat`,
                    { session_id: sessionId, message: q, lang },
                    { timeout: 45000 },
                );
                setMessages((prev) => [
                    ...prev,
                    { role: "assistant", content: data.answer },
                ]);
                if (data.session_id && data.session_id !== sessionId) {
                    setSessionId(data.session_id);
                }
            } catch (e) {
                const msg =
                    e?.response?.data?.detail || c.errorGeneric;
                setErr(typeof msg === "string" ? msg : c.errorGeneric);
            } finally {
                setPending(false);
            }
        },
        [c.errorGeneric, lang, pending, sessionId],
    );

    const onSubmit = (e) => {
        e.preventDefault();
        send(draft);
    };

    const onKey = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            send(draft);
        }
    };

    const resetChat = () => {
        setMessages([]);
        setErr("");
        setSessionId(genSessionId());
        try {
            window.localStorage.removeItem(STORAGE_KEY);
        } catch {}
    };

    const suggestions = useMemo(() => c.suggestions || [], [c.suggestions]);
    const showIntro = messages.length === 0;

    return (
        <>
            {/* FAB — Chat */}
            <button
                type="button"
                aria-haspopup="dialog"
                aria-expanded={open}
                aria-label={c.openLabel}
                onClick={() => setOpen((v) => !v)}
                data-testid="chat-fab"
                className="fixed bottom-[88px] right-5 z-50 inline-flex items-center gap-2 rounded-full bg-tinta pr-4 pl-3 py-3 text-white shadow-[0_18px_40px_-12px_rgba(20,35,64,0.6)] transition-transform hover:scale-[1.04] focus-visible:outline-ambar"
            >
                <span className="relative flex h-6 w-6 items-center justify-center rounded-sm bg-coral text-white">
                    <MessageCircle size={14} strokeWidth={2.4} />
                </span>
                <span
                    className="text-sm font-semibold hidden sm:inline"
                    style={{ fontStretch: "112%" }}
                >
                    {c.fabTitle}
                </span>
            </button>

            {/* Panel */}
            <AnimatePresence>
                {open && (
                    <>
                        {/* mobile backdrop */}
                        <motion.div
                            key="chat-backdrop"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            className="fixed inset-0 z-[55] bg-tinta/40 backdrop-blur-sm md:hidden"
                            onClick={() => setOpen(false)}
                        />
                        <motion.div
                            key="chat-panel"
                            role="dialog"
                            aria-label={c.title}
                            data-testid="chat-panel"
                            initial={{ opacity: 0, y: 24, scale: 0.98 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 24, scale: 0.98 }}
                            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
                            className="fixed inset-x-0 bottom-0 top-16 z-[60] flex flex-col overflow-hidden rounded-t-2xl bg-white shadow-[0_30px_80px_-20px_rgba(20,35,64,0.5)] md:inset-auto md:bottom-5 md:right-5 md:top-auto md:h-[600px] md:w-[400px] md:rounded-2xl"
                        >
                            {/* Header */}
                            <header className="flex items-center justify-between gap-3 border-b border-linea bg-tinta px-5 py-4 text-white">
                                <div className="flex items-center gap-3">
                                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-tinta-2">
                                        <PentoAvatar size={16} />
                                    </div>
                                    <div>
                                        <div
                                            className="text-[0.98rem] font-semibold leading-tight"
                                            style={{ fontStretch: "112%", fontWeight: 640 }}
                                            data-testid="chat-title"
                                        >
                                            {c.title}
                                        </div>
                                        <div className="text-xs text-[#C5CDD8]">
                                            {c.subtitle}
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-1">
                                    {messages.length > 0 && (
                                        <button
                                            type="button"
                                            onClick={resetChat}
                                            aria-label={c.clear}
                                            title={c.clear}
                                            data-testid="chat-reset"
                                            className="flex h-9 w-9 items-center justify-center rounded-full text-white/70 transition-colors hover:bg-tinta-2 hover:text-white"
                                        >
                                            <RotateCcw size={16} />
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => setOpen(false)}
                                        aria-label={c.closeLabel}
                                        data-testid="chat-close"
                                        className="flex h-9 w-9 items-center justify-center rounded-full text-white/80 transition-colors hover:bg-tinta-2 hover:text-white"
                                    >
                                        <X size={18} />
                                    </button>
                                </div>
                            </header>

                            {/* Log */}
                            <div
                                ref={logRef}
                                className="flex-1 overflow-y-auto bg-niebla/40 px-4 py-5"
                                aria-live="polite"
                                data-testid="chat-log"
                            >
                                {showIntro && (
                                    <>
                                        <ChatBubble role="assistant" html={md(c.intro)} intro />
                                        <div
                                            className="mt-3 flex flex-wrap gap-2"
                                            data-testid="chat-suggestions"
                                        >
                                            {suggestions.map((s, i) => (
                                                <button
                                                    key={i}
                                                    type="button"
                                                    onClick={() => send(s)}
                                                    data-testid={`chat-suggestion-${i}`}
                                                    className="rounded-full border border-linea bg-white px-3 py-1.5 text-xs font-medium text-tinta transition-colors hover:border-coral-profundo hover:bg-coral-profundo hover:text-white"
                                                >
                                                    {s}
                                                </button>
                                            ))}
                                        </div>
                                    </>
                                )}

                                {messages.map((m, i) => (
                                    <ChatBubble
                                        key={i}
                                        role={m.role}
                                        html={m.role === "assistant" ? md(m.content) : esc(m.content)}
                                    />
                                ))}

                                {pending && (
                                    <ChatBubble
                                        role="assistant"
                                        testId="chat-typing"
                                        html={`<span class="pt-chat-typing" aria-label="${c.typing}"><i></i><i></i><i></i></span>`}
                                    />
                                )}

                                {err && (
                                    <div
                                        className="mt-3 rounded-lg border border-[#F2B134]/40 bg-[#FFF4DC] p-3 text-[0.9rem] text-tinta"
                                        data-testid="chat-error"
                                    >
                                        <div className="mb-2">{err}</div>
                                        <a
                                            href={waUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center gap-1 text-sm font-semibold text-coral-profundo hover:underline"
                                        >
                                            {c.errorFallbackCta}
                                            <ExternalLink size={12} />
                                        </a>
                                    </div>
                                )}
                            </div>

                            {/* Composer */}
                            <form
                                onSubmit={onSubmit}
                                className="border-t border-linea bg-white px-4 pt-3"
                                data-testid="chat-form"
                            >
                                <div className="flex items-end gap-2">
                                    <textarea
                                        ref={inputRef}
                                        value={draft}
                                        onChange={(e) => setDraft(e.target.value)}
                                        onKeyDown={onKey}
                                        rows={1}
                                        maxLength={1500}
                                        placeholder={c.placeholder}
                                        aria-label={c.placeholder}
                                        data-testid="chat-input"
                                        className="pt-chat-textarea"
                                    />
                                    <button
                                        type="submit"
                                        disabled={pending || !draft.trim()}
                                        aria-label={c.send}
                                        data-testid="chat-send"
                                        className="flex h-10 w-10 flex-none items-center justify-center rounded-lg bg-coral-profundo text-white transition-all hover:bg-coral-oscuro disabled:cursor-not-allowed disabled:opacity-40"
                                    >
                                        <Send size={16} />
                                    </button>
                                </div>
                                <p className="mt-2 mb-3 text-[0.72rem] leading-snug text-acero">
                                    {c.disclaimer}
                                </p>
                            </form>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
        </>
    );
};

const ChatBubble = ({ role, html, intro = false, testId }) => {
    const isUser = role === "user";
    return (
        <div
            className={`mb-3 flex ${isUser ? "justify-end" : "justify-start"}`}
            data-testid={testId || `chat-msg-${role}`}
        >
            <div
                className={`pt-chat-bubble max-w-[85%] rounded-2xl px-4 py-2.5 text-[0.94rem] leading-relaxed ${
                    isUser
                        ? "rounded-br-md bg-tinta text-white"
                        : "rounded-bl-md border border-linea bg-white text-tinta"
                }`}
                dangerouslySetInnerHTML={{ __html: html }}
            />
        </div>
    );
};
